import { useEffect, useState } from "react";
import {
  Plus,
  PencilSimple,
  Trash,
  FloppyDisk,
  X,
  Storefront,
  Question,
} from "@phosphor-icons/react";
import {
  fetchFaqs,
  createFaq,
  updateFaq,
  deleteFaq,
  fetchBusinessProfile,
  updateBusinessProfile,
} from "@/lib/api";
import { toast } from "sonner";

export default function Knowledge() {
  const [faqs, setFaqs] = useState([]);
  const [profile, setProfile] = useState(null);
  const [newQ, setNewQ] = useState("");
  const [newA, setNewA] = useState("");
  const [editing, setEditing] = useState(null);
  const [editQ, setEditQ] = useState("");
  const [editA, setEditA] = useState("");

  const load = async () => {
    const [f, p] = await Promise.all([fetchFaqs(), fetchBusinessProfile()]);
    setFaqs(f);
    setProfile(p);
  };

  useEffect(() => {
    load();
  }, []);

  const handleAdd = async () => {
    if (!newQ.trim() || !newA.trim()) return;
    await createFaq({ question: newQ.trim(), answer: newA.trim() });
    setNewQ("");
    setNewA("");
    toast.success("FAQ added.");
    load();
  };

  const startEdit = (f) => {
    setEditing(f.id);
    setEditQ(f.question);
    setEditA(f.answer);
  };

  const handleUpdate = async () => {
    await updateFaq(editing, { question: editQ, answer: editA });
    setEditing(null);
    toast.success("FAQ updated.");
    load();
  };

  const handleDelete = async (id) => {
    await deleteFaq(id);
    toast.success("FAQ deleted.");
    load();
  };

  const handleProfileSave = async () => {
    const saved = await updateBusinessProfile(profile);
    setProfile(saved);
    toast.success("Business profile saved.");
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 lg:py-8 max-w-5xl mx-auto" data-testid="knowledge-page">
      <div className="mb-8">
        <div className="overline">Wissensdatenbank</div>
        <h1 className="font-semibold text-3xl sm:text-4xl tracking-tight mt-2">
          Bring deinem Bot was bei
        </h1>
        <p className="text-muted-foreground mt-3 max-w-2xl leading-relaxed">
          Alles hier wird vom Bot als verbindliche Quelle benutzt. Wenn eine
          Frage nicht abgedeckt ist, wird sie an dich eskaliert.
        </p>
      </div>

      {/* Business Profile */}
      <section className="card-soft mb-6 overflow-hidden" data-testid="business-profile-section">
        <div className="flex items-center gap-3 px-5 py-4 border-b border-border-soft bg-secondary/30">
          <Storefront size={18} weight="duotone" className="text-muted-foreground" />
          <div className="overline">Geschäftsprofil</div>
        </div>
        <div className="p-4 sm:p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          {[
            { key: "name", label: "Name des Unternehmens", placeholder: "Surft Beach Düren" },
            { key: "hours", label: "Öffnungszeiten", placeholder: "Mo–So 10:00–22:00" },
            { key: "contact", label: "Telefon / Kontakt", placeholder: "+49 2421 1234567" },
            { key: "address", label: "Adresse", placeholder: "Echtzer Str. 100, 52355 Düren" },
          ].map((field) => (
            <div key={field.key}>
              <label className="overline block mb-2">{field.label}</label>
              <input
                data-testid={`profile-${field.key}`}
                type="text"
                value={profile?.[field.key] || ""}
                onChange={(e) =>
                  setProfile({ ...profile, [field.key]: e.target.value })
                }
                placeholder={field.placeholder}
                className="w-full border border-border-soft rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
              />
            </div>
          ))}
          <div className="md:col-span-2">
            <label className="overline block mb-2">Kurzbeschreibung</label>
            <textarea
              data-testid="profile-description"
              rows={2}
              value={profile?.description || ""}
              onChange={(e) =>
                setProfile({ ...profile, description: e.target.value })
              }
              placeholder="Wir sind ein Strand-Restaurant am Badesee mit Wassersport und Events."
              className="w-full border border-border-soft rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 resize-y transition-all"
            />
          </div>
          <div className="md:col-span-2">
            <label className="overline block mb-2">
              Zusatz-Kontext & Hausregeln
            </label>
            <textarea
              data-testid="profile-additional"
              rows={6}
              value={profile?.additional_info || ""}
              onChange={(e) =>
                setProfile({ ...profile, additional_info: e.target.value })
              }
              placeholder="Hunde an der Leine erlaubt. Stornierung bis 24h vorher kostenlos. Eigenes Essen außerhalb des Gastro-Bereichs willkommen."
              className="w-full border border-border-soft rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 resize-y transition-all"
            />
          </div>
          <div className="md:col-span-2">
            <label className="overline block mb-2">
              Dokument-Inhalt einfügen (optional)
            </label>
            <textarea
              data-testid="profile-docs"
              rows={6}
              value={profile?.documents_text || ""}
              onChange={(e) =>
                setProfile({ ...profile, documents_text: e.target.value })
              }
              placeholder="Füg hier den Text von Speisekarten, AGB, Veranstaltungs-Übersichten o.ä. ein. Der Bot nutzt das als Kontext."
              className="w-full border border-border-soft rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 resize-y font-mono transition-all"
            />
          </div>
        </div>
        <div className="flex items-center justify-end p-4 border-t border-border-soft bg-secondary/20">
          <button
            className="btn-invert"
            onClick={handleProfileSave}
            data-testid="save-profile-btn"
          >
            <FloppyDisk size={16} weight="bold" /> Profil speichern
          </button>
        </div>
      </section>

      {/* FAQs */}
      <section className="card-soft overflow-hidden" data-testid="faqs-section">
        <div className="flex items-center gap-3 px-5 py-4 border-b border-border-soft bg-secondary/30">
          <Question size={18} weight="duotone" className="text-muted-foreground" />
          <div className="overline">FAQs · {faqs.length}</div>
        </div>

        <div className="p-4 sm:p-6 border-b border-border-soft grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-4 items-start">
          <input
            type="text"
            data-testid="new-faq-question"
            value={newQ}
            onChange={(e) => setNewQ(e.target.value)}
            placeholder="Neue Frage (z.B. Wann habt ihr offen?)"
            className="border border-border-soft rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
          />
          <input
            type="text"
            data-testid="new-faq-answer"
            value={newA}
            onChange={(e) => setNewA(e.target.value)}
            placeholder="Antwort"
            className="border border-border-soft rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
          />
          <button
            className="btn-invert"
            onClick={handleAdd}
            data-testid="add-faq-btn"
          >
            <Plus size={16} weight="bold" /> Hinzufügen
          </button>
        </div>

        <div>
          {faqs.length === 0 && (
            <div className="p-10 text-center text-sm text-muted-foreground">
              Noch keine FAQs. Leg oben deine erste Frage+Antwort an.
            </div>
          )}
          {faqs.map((f) => (
            <div
              key={f.id}
              className="p-4 sm:p-6 border-b border-border"
              data-testid={`faq-${f.id}`}
            >
              {editing === f.id ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <input
                    value={editQ}
                    onChange={(e) => setEditQ(e.target.value)}
                    className="border border-border-soft rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
                  />
                  <input
                    value={editA}
                    onChange={(e) => setEditA(e.target.value)}
                    className="border border-border-soft rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
                  />
                  <div className="md:col-span-2 flex gap-2 justify-end">
                    <button
                      className="btn-ghost"
                      onClick={() => setEditing(null)}
                    >
                      <X size={14} weight="bold" /> Abbrechen
                    </button>
                    <button className="btn-invert" onClick={handleUpdate}>
                      <FloppyDisk size={14} weight="bold" /> Speichern
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="font-display font-bold text-lg tracking-tight">
                      {f.question}
                    </div>
                    <div className="text-muted-foreground mt-1 leading-relaxed">
                      {f.answer}
                    </div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <button
                      className="border border-border p-2 hover:bg-muted"
                      onClick={() => startEdit(f)}
                      data-testid={`edit-faq-${f.id}`}
                      aria-label="Edit"
                    >
                      <PencilSimple size={14} weight="bold" />
                    </button>
                    <button
                      className="border border-border p-2 hover:bg-destructive hover:text-white hover:border-destructive"
                      onClick={() => handleDelete(f.id)}
                      data-testid={`delete-faq-${f.id}`}
                      aria-label="Delete"
                    >
                      <Trash size={14} weight="bold" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
